/**
 * Session Context for managing session expiry warnings and refresh.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { refreshSession } from "../api/generated";
import type { SessionRefreshResponse } from "../api/generated.schemas";
import { auth } from "../config/firebase";

interface SessionContextValue {
  expiresAt: Date | null;
  showWarning: boolean;
  isRefreshing: boolean;
  extendSession: () => Promise<void>;
  dismissWarning: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionExpiresAt, user, updateSessionExpiry } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check for upcoming expiry every minute
  useEffect(() => {
    if (!sessionExpiresAt || !user) {
      setShowWarning(false);
      return;
    }

    const checkExpiry = () => {
      const timeUntilExpiry = sessionExpiresAt.getTime() - Date.now();

      if (timeUntilExpiry <= WARNING_THRESHOLD_MS && timeUntilExpiry > 0) {
        setShowWarning(true);
      } else if (timeUntilExpiry <= 0) {
        // Session has expired, warning will be dismissed by 401 redirect
        setShowWarning(false);
      }
    };

    // Check immediately
    checkExpiry();

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);

    return () => clearInterval(interval);
  }, [sessionExpiresAt, user]);

  const extendSession = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Get a fresh Firebase ID token
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error("No Firebase user");
      }

      const token = await firebaseUser.getIdToken(true);

      // Call the refresh endpoint (the orval mutator returns the response body)
      const data = (await refreshSession({
        firebase_token: token,
      })) as unknown as SessionRefreshResponse;

      // Update session expiry from the refresh response
      if (data.session_expires_at) {
        updateSessionExpiry(new Date(data.session_expires_at));
      }
      setShowWarning(false);
    } catch (error) {
      console.error("Failed to extend session:", error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [updateSessionExpiry]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        expiresAt: sessionExpiresAt,
        showWarning,
        isRefreshing,
        extendSession,
        dismissWarning,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
